import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Grid3X3, LayoutGrid } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ContactSheet from "@/components/ContactSheet";
import CaseStudyCard from "@/components/CaseStudyCard";
import type { CaseStudyCardData } from "@/components/CaseStudyCard";
import FooterSection from "@/components/FooterSection";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import SEO from "@/components/SEO";

interface Study {
  slug: string;
  title: string;
  subtitle: string | null;
  category: string | null;
  cover_image_url: string | null;
  logo_url: string | null;
  tags: string[] | null;
  industry_tag: string | null;
  year: string | null;
}

interface ClientProject {
  company: string;
  focus: string;
  year: string;
  category: string;
  industry_tag: string | null;
  link: string | null;
  case_study_id: string | null;
}

const categoryOrder = [
  "Framer Web Development",
  "E-Commerce Design & Development",
  "Squarespace Web Development",
  "Marketplace Product Creation",
  "Product Design",
  "Copywriting",
  "Email Design",
];

/* ─── Filter dropdown ─── */
const FilterDropdown = ({
  value,
  onChange,
  options,
  labelMap,
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  labelMap: (v: string) => string;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        className={`font-body text-ts-2xs tracking-[0.1em] uppercase px-2.5 py-1.5 border transition-all duration-300 flex items-center gap-2 ${
          value !== "All"
            ? "bg-foreground text-background border-foreground"
            : "border-border text-muted-foreground"
        }`}
      >
        {labelMap(value)}
        <svg
          className={`w-2 h-2 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 10 6"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="m1 1 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-background border border-border shadow-lg min-w-[200px] max-h-[260px] overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-2 font-body text-ts-2xs tracking-wide transition-colors ${
                value === opt
                  ? "bg-foreground text-background font-medium"
                  : "text-foreground hover:bg-muted"
              }`}
            >
              {labelMap(opt)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CaseStudies = () => {
  const [studies, setStudies] = useState<Study[]>([]);
  const [clients, setClients] = useState<ClientProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string>("All");
  const [activeIndustry, setActiveIndustry] = useState<string>("All");
  const [activeYear, setActiveYear] = useState<string>("All");
  const [columns, setColumns] = useState<3 | 4>(() => {
    const saved = localStorage.getItem("caseStudiesColumns");
    return (saved === "3" ? 3 : 4) as 3 | 4;
  });
  const [page, setPage] = useState(1);
  const isMobile = useIsMobile();
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPausedRef = useRef(false);

  const scrollByCard = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 10;
    if (atEnd) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      el.scrollBy({ left: 300, behavior: "smooth" });
    }
  }, []);

  const startAutoPlay = useCallback(() => {
    if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    autoTimerRef.current = setInterval(() => {
      if (isPausedRef.current) return;
      scrollByCard();
    }, 5000);
  }, [scrollByCard]);

  const pauseAuto = useCallback(() => { isPausedRef.current = true; }, []);
  const resumeAuto = useCallback(() => {
    isPausedRef.current = false;
    startAutoPlay();
  }, [startAutoPlay]);

  useEffect(() => {
    if (!isMobile) return;
    startAutoPlay();
    return () => { if (autoTimerRef.current) clearInterval(autoTimerRef.current); };
  }, [isMobile, startAutoPlay]);

  /* ─── Data fetching ─── */
  useEffect(() => {
    const hiddenSlugs = ["empire"];
    const fetchAll = async () => {
      const [studiesRes, clientsRes] = await Promise.all([
        supabase
          .from("case_studies")
          .select("slug, title, subtitle, category, cover_image_url, logo_url, tags, industry_tag, year")
          .order("display_order", { ascending: true }),
        supabase
          .from("client_projects")
          .select("company, focus, year, category, industry_tag, link, case_study_id")
          .order("display_order", { ascending: true }),
      ]);
      if (!studiesRes.error && studiesRes.data) {
        setStudies(studiesRes.data.filter((s) => !hiddenSlugs.includes(s.slug)));
      }
      if (!clientsRes.error && clientsRes.data) setClients(clientsRes.data);
      setLoading(false);
    };
    fetchAll();
  }, []);

  /* ─── Unified filter options from both datasets ─── */
  const categories = useMemo(() => {
    const cats = new Set<string>();
    studies.forEach((s) => s.category && cats.add(s.category));
    clients.forEach((c) => c.category && cats.add(c.category));
    const ordered = categoryOrder.filter((c) => cats.has(c));
    for (const c of cats) {
      if (!categoryOrder.includes(c)) ordered.push(c);
    }
    return ["All", ...ordered];
  }, [studies, clients]);

  const industries = useMemo(() => {
    const set = new Set<string>();
    studies.forEach((s) => s.industry_tag && set.add(s.industry_tag));
    clients.forEach((c) => c.industry_tag && set.add(c.industry_tag));
    return ["All", ...Array.from(set).sort()];
  }, [studies, clients]);

  const years = useMemo(() => {
    const set = new Set<string>();
    studies.forEach((s) => {
      if (s.year) set.add(s.year);
    });
    clients.forEach((c) => set.add(c.year));
    return ["All", ...Array.from(set).sort().reverse()];
  }, [studies, clients]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    studies.forEach((s) => {
      if (s.category) counts[s.category] = (counts[s.category] || 0) + 1;
    });
    // Include client projects that don't have a case study (no duplicates)
    clients.forEach((c) => {
      if (!c.case_study_id && c.category) {
        counts[c.category] = (counts[c.category] || 0) + 1;
      }
    });
    return counts;
  }, [studies, clients]);

  /* ─── Filtered case studies ─── */
  const filtered = useMemo(() => {
    return studies
      .filter((s) => {
        if (activeFilter !== "All" && s.category !== activeFilter) return false;
        if (activeIndustry !== "All" && s.industry_tag !== activeIndustry) return false;
        if (activeYear !== "All" && s.year !== activeYear) return false;
        return true;
      })
      .sort((a, b) => (b.year || "").localeCompare(a.year || ""));
  }, [studies, activeFilter, activeIndustry, activeYear]);

  /* ─── Filtered client projects (no case study attached) ─── */
  const filteredClients = useMemo(() => {
    return clients
      .filter((c) => !c.case_study_id)
      .filter((c) => {
        if (activeFilter !== "All" && c.category !== activeFilter) return false;
        if (activeIndustry !== "All" && c.industry_tag !== activeIndustry) return false;
        if (activeYear !== "All" && c.year !== activeYear) return false;
        return true;
      });
  }, [clients, activeFilter, activeIndustry, activeYear]);

  /* Group clients by category */
  const clientsByCategory = useMemo(() => {
    const grouped = new Map<string, ClientProject[]>();
    for (const c of filteredClients) {
      const list = grouped.get(c.category) || [];
      list.push(c);
      grouped.set(c.category, list);
    }
    const sections: { heading: string; projects: ClientProject[] }[] = [];
    for (const cat of categoryOrder) {
      if (grouped.has(cat)) {
        sections.push({ heading: cat, projects: grouped.get(cat)!.sort((a, b) => b.year.localeCompare(a.year)) });
        grouped.delete(cat);
      }
    }
    for (const [cat, projects] of grouped) {
      sections.push({ heading: cat, projects: projects.sort((a, b) => b.year.localeCompare(a.year)) });
    }
    return sections;
  }, [filteredClients]);

  const paginatedMobile = useMemo(
    () => filtered.slice(0, page * 10),
    [filtered, page]
  );

  useEffect(() => { setPage(1); }, [activeFilter, activeIndustry, activeYear]);
  useEffect(() => { localStorage.setItem("caseStudiesColumns", columns.toString()); }, [columns]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Work"
        description="Dive into selected projects — strategy, design decisions, and outcomes. Plus a full index of past clients."
        canonical="/work"
      />

      {/* Top nav */}
      <header className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-border">
        <Link
          to="/"
          className="flex items-center gap-2 font-body text-ts-2xs tracking-[0.15em] uppercase opacity-40 hover:opacity-100 transition-opacity"
        >
          <ArrowLeft size={12} />
          Back
        </Link>
        <ContactSheet>
          <button className="font-body text-ts-2xs tracking-[0.15em] uppercase opacity-40 hover:opacity-100 transition-opacity">
            Work With Us
          </button>
        </ContactSheet>
      </header>

      <div className="px-5 md:px-8 lg:px-12 py-8 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="font-display text-[clamp(2rem,5vw,4rem)] italic leading-[0.95] tracking-tight">
            Work
          </h1>
          <p className="font-body text-ts-2xs text-muted-foreground mt-2 max-w-md">
            Selected case studies and a full index of past collaborations.
          </p>
        </motion.div>

        {/* ─── Filter bar ─── */}
        <div className="flex flex-col gap-4 mb-8 border-b border-border pb-4">
          <div className="flex items-center justify-between">
            {/* Category chips — dropdown on mobile, chips on desktop */}
            <div className="flex flex-wrap gap-1.5 items-center">
              <FilterDropdown
                value={activeFilter}
                onChange={setActiveFilter}
                options={categories}
                labelMap={(v) => v === "All" ? `All (${studies.length + clients.filter(c => !c.case_study_id).length})` : `${v} (${categoryCounts[v] || 0})`}
                className="md:hidden"
              />
              <div className="hidden md:flex flex-wrap gap-1.5">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveFilter(cat)}
                    className={`font-body text-ts-2xs tracking-[0.1em] uppercase px-2.5 py-1.5 border transition-all duration-300 ${
                      activeFilter === cat
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
                    }`}
                  >
                    {cat} {cat !== "All" && `(${categoryCounts[cat] || 0})`}
                  </button>
                ))}
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-1">
              <button
                onClick={() => setColumns(3)}
                className={`p-1.5 transition-opacity ${columns === 3 ? "opacity-100" : "opacity-30 hover:opacity-60"}`}
                aria-label="3 columns"
              >
                <Grid3X3 size={14} />
              </button>
              <button
                onClick={() => setColumns(4)}
                className={`p-1.5 transition-opacity ${columns === 4 ? "opacity-100" : "opacity-30 hover:opacity-60"}`}
                aria-label="4 columns"
              >
                <LayoutGrid size={14} />
              </button>
            </div>
          </div>

          {/* Secondary filters: Industry + Year */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="font-body text-[10px] tracking-[0.15em] uppercase text-muted-foreground">Industry</span>
              <FilterDropdown
                value={activeIndustry}
                onChange={setActiveIndustry}
                options={industries}
                labelMap={(v) => v === "All" ? "All" : v}
              />
            </div>

            {years.length > 2 && (
              <div className="flex items-center gap-2">
                <span className="font-body text-[10px] tracking-[0.15em] uppercase text-muted-foreground">Year</span>
                <FilterDropdown
                  value={activeYear}
                  onChange={setActiveYear}
                  options={years}
                  labelMap={(v) => v === "All" ? "All" : v}
                />
              </div>
            )}
          </div>

          {/* Result count */}
          {(activeFilter !== "All" || activeIndustry !== "All" || activeYear !== "All") && (
            <div className="mt-3">
              <span className="font-body text-[10px] tracking-[0.15em] uppercase text-muted-foreground/50">
                {filtered.length + filteredClients.length} of {studies.length + clients.filter(c => !c.case_study_id).length} projects
              </span>
            </div>
          )}
        </div>

        {/* ─── Case Studies Grid / Carousel ─── */}
        {loading ? (
          <p className="font-body text-ts-2xs text-muted-foreground">Loading…</p>
        ) : (
          <>
            {/* Case studies section */}
            {filtered.length > 0 && (
              <>
              <h2 className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground/50 mb-6">
                Case Studies
                <span className="ml-2 text-muted-foreground/30">({filtered.length})</span>
              </h2>
              {isMobile ? (
                <div>
                  <div
                    ref={scrollRef}
                    onTouchStart={pauseAuto}
                    onTouchEnd={() => setTimeout(resumeAuto, 8000)}
                    className="flex gap-4 overflow-x-auto pb-6 snap-x snap-mandatory -mx-5 px-5"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                  >
                    {paginatedMobile.map((study, i) => {
                      const mapped: CaseStudyCardData = {
                        slug: study.slug,
                        title: study.title,
                        subtitle: study.subtitle,
                        category: study.category,
                        industry_tag: study.industry_tag,
                        image: study.cover_image_url || "",
                        logo: study.logo_url || "",
                      };
                      return (
                        <motion.div
                          key={study.slug}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.04, duration: 0.3 }}
                          className="flex-shrink-0 w-[75vw] max-w-[320px] snap-center"
                        >
                          <CaseStudyCard study={mapped} />
                        </motion.div>
                      );
                    })}
                  </div>
                  {filtered.length > page * 10 && (
                    <div className="flex justify-center mt-2">
                      <button
                        onClick={() => setPage((p) => p + 1)}
                        className="font-body text-ts-2xs tracking-[0.15em] uppercase border border-border px-4 py-2 hover:bg-foreground hover:text-background transition-colors"
                      >
                        Show more
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <motion.div
                  layout
                  className={`grid grid-cols-1 sm:grid-cols-2 ${columns === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-6 md:gap-8`}
                >
                  <AnimatePresence mode="popLayout">
                    {filtered.map((study) => {
                      const mapped: CaseStudyCardData = {
                        slug: study.slug,
                        title: study.title,
                        subtitle: study.subtitle,
                        category: study.category,
                        industry_tag: study.industry_tag,
                        image: study.cover_image_url || "",
                        logo: study.logo_url || "",
                      };
                      return (
                        <motion.div
                          key={study.slug}
                          layout
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.25 }}
                        >
                          <CaseStudyCard study={mapped} />
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </motion.div>
              )}
              </>
            )}

            {filtered.length === 0 && filteredClients.length === 0 && (
              <p className="font-body text-ts-xs text-muted-foreground/60 py-8 text-center">
                No projects match the selected filters.
              </p>
            )}

            {/* ─── Client Index ─── */}
            {filteredClients.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.15 }}
                className={filtered.length > 0 ? "mt-12 pt-10 border-t border-border" : ""}
              >
                <h2 className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground/50 mb-6">
                  Additional Clients
                  <span className="ml-2 text-muted-foreground/30">({filteredClients.length})</span>
                </h2>

                <div className="columns-1 md:columns-2 gap-x-10">
                  {clientsByCategory.map((section, sIdx) => (
                    <motion.section
                      key={section.heading}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: sIdx * 0.06, duration: 0.4 }}
                      className="mb-6 break-inside-avoid"
                    >
                      <h3 className="font-body text-ts-2xs font-bold tracking-[0.3em] uppercase opacity-35 mb-2">
                        {section.heading}
                      </h3>
                      {section.projects.map((project, pIdx) => {
                        const content = (
                          <>
                            <span className="font-body text-ts-xs opacity-50 tabular-nums shrink-0 w-10">
                              {project.year}
                            </span>
                            <span
                              className="font-heading text-ts-xs md:text-ts-sm tracking-tight shrink-0"
                              style={{ fontWeight: 500 }}
                            >
                              {project.company}
                            </span>
                            <span className="font-body text-ts-xs opacity-50 truncate">
                              {project.focus}
                            </span>
                          </>
                        );
                        return project.link ? (
                          <a
                            key={`${project.company}-${pIdx}`}
                            href={project.link}
                            target={project.link.startsWith("/") ? undefined : "_blank"}
                            rel={project.link.startsWith("/") ? undefined : "noopener noreferrer"}
                            className="flex items-baseline gap-3 py-[5px] group cursor-pointer hover:opacity-80 transition-opacity"
                          >
                            {content}
                            <span className="font-body text-ts-2xs opacity-0 group-hover:opacity-40 transition-opacity tracking-[0.15em] uppercase ml-auto shrink-0">
                              View ↗
                            </span>
                          </a>
                        ) : (
                          <div
                            key={`${project.company}-${pIdx}`}
                            className="flex items-baseline gap-3 py-[5px] group"
                          >
                            {content}
                          </div>
                        );
                      })}
                    </motion.section>
                  ))}
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>

      <FooterSection />
    </div>
  );
};

export default CaseStudies;
